/**
 * MyTasksPage
 * Personal task inbox for clinicians.
 * Shows assigned tasks grouped by priority: Overdue → Pinned → Blocked → Available → Completed.
 */

import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { SummaryCard, SummaryCardsRow, CollapsibleSection, workflowTypeLabel } from '../../components/tasks';
import { Eye } from 'lucide-react';
import { MyTaskDetailPanel } from './MyTaskDetailPanel';
import { useTaskUIStore } from '../../stores/taskUIStore';
import { useTaskList, useTogglePin } from '../../hooks/useTasks';
import { MyTaskRow } from './MyTaskRow';
import { useAuthStore } from '../../stores/authStore';
import { theme } from '../../styles/theme';
import { MyTasksFilterBar } from './MyTasksFilterBar';
import { MyTasksNewTaskForm } from './MyTasksNewTaskForm';
import { PrioritySectionList } from './PrioritySectionList';

// ============================================================================
// Styled components
// ============================================================================

const PageWrapper = styled.main`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const NewTaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.gradients.button};
  color: ${({ theme }) => theme.colors.text.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[12]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[8]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const PaginationInfo = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin: 0;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;


// ============================================================================
// Component
// ============================================================================

const MyTasksPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    searchParams.get('taskId'),
  );

  // Keep the modal in sync when the URL ?taskId= param changes after mount
  // (e.g. when a notification deep-link is clicked while already on this page).
  useEffect(() => {
    setSelectedTaskId(searchParams.get('taskId'));
  }, [searchParams]);

  const {
    myTasksFormOpen,
    myTasksWorkflowFilter,
    myTasksStatusFilter,
    toggleMyTasksForm,
    setMyTasksWorkflowFilter,
    setMyTasksStatusFilter,
    expandedSections,
    toggleSection,
  } = useTaskUIStore();

  // Get the current user so we can scope the task list to their assignments only
  const user = useAuthStore((state) => state.user);
  const hasPermission = useAuthStore((state) => state.hasPermission);

  // `scope: 'mine'` makes the server pin the list to the caller (ADO 577); the
  // `enabled` gate stops a pre-auth fetch from going out without any caller filter.
  const { data: taskList, isLoading, isError } = useTaskList(
    {
      scope: 'mine',
      assigneeId: user?.id,
      status: myTasksStatusFilter !== 'ALL' ? myTasksStatusFilter : undefined,
      workflowType: myTasksWorkflowFilter !== 'all' ? myTasksWorkflowFilter : undefined,
      limit: 100,
      sortBy: 'sortBand',
      sortOrder: 'asc',
    },
    { enabled: !!user?.id },
  );

  const { data: watchingList } = useTaskList(
    { watchedByUserId: user?.id, limit: 100, sortBy: 'scheduledDate', sortOrder: 'asc' },
    { enabled: !!user?.id },
  );

  const togglePin = useTogglePin();

  const workflowOptions = useMemo(() => {
    if (!taskList?.data) return [];
    const types = new Set(
      taskList.data
        .map((t) => t.workflowInstance?.workflowType)
        .filter((type): type is string => Boolean(type)),
    );
    return Array.from(types).map((t) => ({ value: t, label: workflowTypeLabel(t) }));
  }, [taskList?.data]);

  // Derive summary counts from the user-scoped task list (not the org-wide summary endpoint)
  const myCounts = useMemo(() => {
    const tasks = taskList?.data || [];
    return {
      overdue: tasks.filter((t) => t.status === 'OVERDUE').length,
      available: tasks.filter((t) => t.status === 'AVAILABLE').length,
      inProgress: tasks.filter((t) => t.status === 'IN_PROGRESS').length,
      slaWarnings: tasks.filter((t) => t.escalationTier !== 'NORMAL').length,
    };
  }, [taskList?.data]);

  return (
    <PageWrapper>
      {/* Page Header */}
      <PageHeader>
        <HeaderText>
          <PageTitle>My Tasks</PageTitle>
          <PageSubtitle>Unified task inbox across all workflows</PageSubtitle>
        </HeaderText>
        {/* Only render the New Task button when the user has task:create permission */}
        {hasPermission('tasks:my_tasks') && (
          <NewTaskButton
            type="button"
            onClick={toggleMyTasksForm}
            aria-label={myTasksFormOpen ? 'Close new task form' : 'Add a new task'}
            aria-expanded={myTasksFormOpen}
          >
            <Plus size={16} aria-hidden="true" />
            New Task
          </NewTaskButton>
        )}
      </PageHeader>

      {/* Inline add-task form (slide-down) */}
      {myTasksFormOpen && <MyTasksNewTaskForm workflowOptions={workflowOptions} />}

      {/* Summary metric cards — counts derived from the user-scoped task list */}
      <SummaryCardsRow>
        <SummaryCard
          label="Overdue"
          value={myCounts.overdue}
          accentColor={theme.colors.status.error.text}
        />
        <SummaryCard
          label="Available"
          value={myCounts.available}
          accentColor={theme.colors.primary.base}
        />
        <SummaryCard
          label="In Progress"
          value={myCounts.inProgress}
          accentColor={theme.colors.status.success.text}
        />
        <SummaryCard
          label="Timeline Warnings"
          value={myCounts.slaWarnings}
          accentColor={theme.colors.status.warning.text}
        />
      </SummaryCardsRow>

      {/* Filter bar */}
      <MyTasksFilterBar
        workflowFilter={myTasksWorkflowFilter}
        statusFilter={myTasksStatusFilter}
        onWorkflowChange={setMyTasksWorkflowFilter}
        onStatusChange={(value) =>
          setMyTasksStatusFilter(value as typeof myTasksStatusFilter)
        }
        workflowOptions={workflowOptions}
      />

      {/* Task list */}
      {isLoading ? (
        <LoadingState role="status" aria-live="polite">
          Loading your tasks...
        </LoadingState>
      ) : isError ? (
        <ErrorState role="alert">
          Failed to load tasks. Please refresh the page.
        </ErrorState>
      ) : (
        <>
          <PrioritySectionList
            tasks={taskList?.data ?? []}
            onTogglePin={(taskId) => togglePin.mutate(taskId)}
            onViewDetails={(taskId) => setSelectedTaskId(taskId)}
          />
          {taskList?.meta && taskList.meta.totalPages > 1 && (
            <PaginationInfo>
              Showing {taskList.data.length} of {taskList.meta.total} tasks
              {/* TODO: Add full pagination controls */}
            </PaginationInfo>
          )}
        </>
      )}

      {/* Watching section — tasks the user watches but is not assigned to */}
      {watchingList && watchingList.data.length > 0 && (
        <CollapsibleSection
          title="Watching"
          count={watchingList.data.length}
          icon={<Eye size={15} aria-hidden="true" />}
          variant="neutral"
          isExpanded={expandedSections.watching}
          onToggle={() => toggleSection('watching')}
        >
          {watchingList.data.map((task) => (
            <MyTaskRow
              key={task.id}
              task={task}
              onViewDetails={(taskId) => setSelectedTaskId(taskId)}
            />
          ))}
        </CollapsibleSection>
      )}

      {/* Task detail panel — opened via onViewDetails on each task row, or ?taskId= deep-link.
          Workflow-linked tasks open the rich TaskDetailPanel; standalone tasks fall back to
          the simpler TaskDetailModal. Both paths are handled inside MyTaskDetailPanel. */}
      {selectedTaskId && (
        <MyTaskDetailPanel
          taskId={selectedTaskId}
          onClose={() => {
            setSelectedTaskId(null);
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete('taskId');
              return next;
            });
          }}
        />
      )}
    </PageWrapper>
  );
};

export default MyTasksPage;
